import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";

/**
 * InlineCTA — Compact inline call-to-action band, thinner than SectionCTA.
 * Designed to be dropped between long editorial blocks to surface up to 2
 * strategic actions ("Demander un devis", "Parlez-nous de votre projet",
 * "Contact", "À propos de nous"...).
 *
 * Props:
 *  - title (string): main line
 *  - text  (string): optional secondary line
 *  - primary  ({ label, to }): main CTA (filled)
 *  - secondary ({ label, to }): optional secondary CTA (ghost)
 *  - variant ("navy" | "light"): background style. Defaults to "light".
 *  - dataTestId (string)
 */
export default function InlineCTA({
    title,
    text,
    primary = { label: "Demander un devis", to: "/contact" },
    secondary = null,
    variant = "light",
    dataTestId = "inline-cta",
}) {
    const isNavy = variant === "navy";

    return (
        <section
            className={
                isNavy
                    ? "bg-navy-700 text-white border-y border-navy-800"
                    : "bg-concrete-50 text-navy-700 border-y border-concrete-200"
            }
            data-testid={dataTestId}
        >
            <div className="rcps-container py-10 md:py-12 grid md:grid-cols-12 gap-6 md:gap-8 items-center">
                <div className="md:col-span-8">
                    <h3
                        className={`font-heading text-xl md:text-2xl font-semibold tracking-tight leading-snug ${
                            isNavy ? "text-white" : "text-navy-700"
                        }`}
                    >
                        {title}
                    </h3>
                    {text && (
                        <p
                            className={`mt-2 text-sm md:text-base max-w-2xl ${
                                isNavy ? "text-white/75" : "text-navy-700/75"
                            }`}
                        >
                            {text}
                        </p>
                    )}
                </div>
                <div className="md:col-span-4 flex flex-wrap md:justify-end items-center gap-3">
                    <Link
                        to={primary.to}
                        data-testid={`${dataTestId}-primary`}
                        className={`inline-flex items-center gap-2 h-11 px-5 rounded-md font-semibold transition ${
                            isNavy
                                ? "bg-white text-navy-700 hover:bg-concrete-50"
                                : "bg-navy-700 text-white hover:bg-navy-800"
                        }`}
                    >
                        {primary.label}
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                    {secondary && (
                        <Link
                            to={secondary.to}
                            data-testid={`${dataTestId}-secondary`}
                            className={`inline-flex items-center gap-2 h-11 px-5 rounded-md font-semibold transition ${
                                isNavy
                                    ? "border border-white/25 text-white hover:bg-white/10"
                                    : "border border-concrete-200 text-navy-700 hover:border-navy-700 hover:bg-white"
                            }`}
                        >
                            {secondary.label}
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    )}
                </div>
            </div>
        </section>
    );
}
